import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useLocale } from "@/contexts/LocaleContext";
import PublicShell from "@/components/public/PublicShell";
import { CheckCircle2, ChevronDown, ChevronRight, ArrowRight } from "lucide-react";

/* ═════════════════════════════════════════════════════════════════════
   Generic public content page. Every route stub (Terms, Privacy,
   Security Centre, FAQ, Refund Policy, etc.) renders through this
   component. Content comes from the PUBLIC_PAGES map below (defaults)
   and can be overridden by an admin via the (upcoming) Legal
   Configuration Centre.
   ═════════════════════════════════════════════════════════════════════ */

// Static registry — admin overrides live in a future backend collection.
// Each entry: { eyebrow, title, subtitle, body: [{h, p, list?}], notice? }
const PAGES = {
    "features": {
        eyebrow: "PRODUCT", title: "Everything VAULT organises for you.",
        subtitle: "Cards, cash, income, expenses, subscriptions, investments, documents, warranties, taxes — one intentional system.",
        body: [
            { h: "Money Control",    p: "Credit cards, expenses, payments, reconciliation, subscriptions, budgets — with real closing controls." },
            { h: "Wealth Overview",  p: "Bank accounts, investments, assets, liabilities and a live net-worth snapshot updated weekly." },
            { h: "Smart Obligations",p: "Bills, renewals, warranties, documents and reminders. Nothing important slips through." },
            { h: "Financial Intelligence", p: "Health score, money-leak detector, spend trends, CA-ready pack, monthly closings and audit trail." },
        ],
    },
    "how-it-works": {
        eyebrow: "HOW IT WORKS", title: "Three calm steps.",
        body: [
            { h: "1. Add or Import", p: "Connect supported sources, upload data or enter details manually." },
            { h: "2. Organise Securely", p: "Bring financial information into one structured, permission-controlled VAULT." },
            { h: "3. Act on Insights", p: "Reminders, reports and actionable information to keep you organised." },
        ],
    },
    "security": {
        eyebrow: "SECURITY CENTRE", title: "Your financial information deserves serious protection.",
        subtitle: "Only capabilities we actually run in every deployment — no marketing fluff.",
        body: [
            { h: "Role-based permissions", p: "Owner, Finance, Entry, Viewer and Auditor roles gate every action." },
            { h: "Masked card information", p: "Only the last four digits are ever displayed in the UI." },
            { h: "No CVV, PIN or OTP storage", p: "We never store or ask for CVV, OTPs, PINs or banking passwords." },
            { h: "Secure authentication", p: "HttpOnly cookies, brute-force protection, optional MFA, forced admin password rotation." },
            { h: "Audit history", p: "Every write is logged with who, what, when and from which IP." },
            { h: "Data export & deletion", p: "Full data export and account deletion available anytime from Settings → Privacy & Data." },
        ],
        notice: "We do not display ISO, SOC, PCI or GDPR certification badges unless independently verified.",
    },
    "privacy-centre": {
        eyebrow: "PRIVACY CENTRE", title: "You own your data. Fully.",
        body: [
            { h: "What we collect", p: "Only what's needed to run the product for you — account, financial records you enter, uploaded documents and support communications." },
            { h: "Never collected", p: "We never store CVV, PIN, OTP, full card magnetic-stripe data or online banking passwords." },
            { h: "Your controls", list: ["Download all your data", "Correct personal information", "Delete your account", "Manage cookie preferences", "Revoke integrations"] },
            { h: "Get in touch", p: "Reach our privacy team at privacy@vaultbypd.com for any data-subject request." },
        ],
    },
    "demo": {
        eyebrow: "LIVE DEMO", title: "Explore VAULT without signing up.",
        subtitle: "One-click access to an isolated demo workspace with dummy data. Anything you add or delete lives only in the demo.",
        body: [{ h: "How the demo works",
            p: "Clicking Explore Demo signs you into a shared demo tenant seeded with sample cards, expenses and bank accounts. It is reset regularly and never mixes with paying customer data.",
            list: ["Isolated tenant, no shared data", "Reset periodically to a clean state", "All modules available — Cards, Cash Desk, Expenses, Reports"] }],
        cta: { label: "Explore Demo →", to: "/login?demo=1" },
    },
    "faq": {
        eyebrow: "FAQ", title: "Answers to the questions we hear most.",
        faq: true, // Renders accordion from /api/public-content/faq
    },
    "terms": {
        eyebrow: "LEGAL", title: "Terms of Service",
        legal: true,
        body: [
            { h: "Nothing you see here is legal advice", p: "This page describes our current draft terms. Final, legally reviewed and approved terms will be published before VAULT accepts paying customers in your region." },
            { h: "Your account", p: "You are responsible for the security of your account and for the accuracy of the information you enter or upload." },
            { h: "What VAULT is not", p: "VAULT is not a bank, payment provider, financial advisor, investment advisor or wealth manager. It does not move, hold, send or receive your money." },
            { h: "Cancellation", p: "You can cancel from Settings → Plan and Billing at any time; cancellation takes effect at the end of your current billing period." },
        ],
    },
    "privacy": {
        eyebrow: "LEGAL", title: "Privacy Policy",
        legal: true,
        body: [
            { h: "Draft notice", p: "This page describes our current draft privacy policy. A final, legally reviewed version will be published before general availability in your region." },
            { h: "Data we collect", p: "Account information, financial records you enter or import, uploaded documents, authentication and device information, and support communications." },
            { h: "How we use it", p: "Solely to provide the VAULT service to you, keep it secure, and support you when you ask us for help." },
            { h: "Your rights", list: ["Access", "Correction", "Portability", "Deletion", "Restriction", "Objection"] },
        ],
    },
    "cookies": {
        eyebrow: "LEGAL", title: "Cookie Policy", legal: true,
        body: [
            { h: "What we use", p: "Only strictly necessary cookies unless you opt in to more from the cookie preferences banner." },
            { h: "Manage anytime", p: "Open Cookie Preferences from the footer to change your choices — no dark patterns, no pre-selected non-essential categories." },
        ],
        cta: { label: "Open Cookie Preferences", event: "vault:cookie-preferences" },
    },
    "billing-terms": {
        eyebrow: "LEGAL", title: "Subscription & Billing Terms", legal: true,
        body: [
            { h: "Payment provider (Merchant of Record)", p: "Payments for VAULT subscriptions are securely processed by Paddle (Paddle.com Market Limited), acting as the Merchant of Record. VAULT does not receive or store your complete card number, card security code, PIN or OTP. Paddle calculates and remits applicable taxes based on your billing location." },
            { h: "Billing frequency", p: "Plans are billed monthly or annually as selected at checkout. All prices are shown before purchase, with taxes calculated by Paddle at checkout based on your location." },
            { h: "Auto-renewal", p: "Subscriptions renew automatically at the end of each billing period until cancelled from Settings → Plan & Billing." },
            { h: "Upgrades & downgrades", p: "Upgrades take effect immediately with prorated billing. Downgrades take effect at the end of the current billing period." },
            { h: "Cancellation", p: "You can cancel at any time from Settings → Plan & Billing → Cancel. Cancellation takes effect at the end of the current paid period." },
            { h: "Refunds", p: "See our Refund & Cancellation Policy for eligibility, timings and how refunds are processed via Paddle." },
        ],
    },
    "privacy": {
        eyebrow: "LEGAL", title: "Privacy Policy", legal: true,
        body: [
            { h: "Who we are", p: "VAULT is operated by PD. This policy describes how we collect, use, share and protect your personal information." },
            { h: "Categories of data", p: "Account details, financial information you enter or import, uploaded documents, cookies and analytics, device signals and support communications." },
            { h: "Purposes", p: "Deliver and improve the product, provide support, run billing (via Paddle), send transactional and (opt-in) marketing communications, and comply with law." },
            { h: "Payment processing (Paddle)", p: "Paddle acts as our Merchant of Record. When you complete checkout, Paddle collects your name, billing address, card details and tax location directly — VAULT never sees your full card number, CVV or PIN. Paddle shares only masked card metadata (brand, last-4, expiry) and subscription lifecycle events with us." },
            { h: "Subprocessors", p: "See /subprocessors for the up-to-date list. Paddle is our billing subprocessor; MongoDB Atlas hosts the database; Emergent LLM powers AI features." },
            { h: "Your rights", p: "Access, correction, deletion, portability and objection rights are available from /privacy-data. See /regional-privacy for jurisdiction-specific disclosures (DPDP, GDPR, CCPA/CPRA)." },
            { h: "Retention", p: "See /data-retention for retention windows. Encrypted backups rotate over 30 days." },
            { h: "Contact", p: "privacy@vaultbypd.com · Grievance escalation at /grievance." },
        ],
    },
    "refund-policy": {
        eyebrow: "LEGAL", title: "Refund & Cancellation Policy", legal: true,
        body: [
            { h: "Cancellation", p: "Cancel any time from Settings → Plan and Billing. Access continues until the end of the current billing period." },
            { h: "Refunds", p: "Refund eligibility depends on plan, region and time since last billing. See the in-app billing centre for the exact terms on your invoice." },
        ],
    },
    "acceptable-use": {
        eyebrow: "LEGAL", title: "Acceptable Use Policy", legal: true,
        body: [
            { h: "You will not", list: [
                "Attempt to access another user's data",
                "Upload malicious content",
                "Use VAULT for illegal purposes",
                "Interfere with the security or availability of the service",
                "Reverse-engineer or scrape the product",
            ] },
        ],
    },
    "financial-disclaimer": {
        eyebrow: "LEGAL", title: "Financial Disclaimer", legal: true,
        body: [
            { h: "Not advice", p: "Nothing shown in VAULT — including insights, health scores, money-leak detections, alerts or recommendations — is financial, legal, tax or investment advice." },
            { h: "Consult a professional", p: "Please consult a qualified professional before acting on financial matters." },
        ],
    },
    "accessibility": {
        eyebrow: "LEGAL", title: "Accessibility Statement", legal: true,
        body: [
            { h: "Our target", p: "VAULT aims to conform to WCAG 2.2 Level AA across the public website and the authenticated application." },
            { h: "Report an issue", p: "If you find something inaccessible, email accessibility@vaultbypd.com. We'll fix it and update this statement." },
        ],
    },
    "subprocessors": {
        eyebrow: "TRUST", title: "Subprocessors",
        body: [
            { h: "Third parties we rely on", p: "The final subprocessor list will be published here after legal review — including cloud infrastructure, payment processing, transactional email and error monitoring." },
        ],
    },
    "status": {
        eyebrow: "TRUST", title: "System Status",
        body: [{ h: "Real-time status", p: "A live status page is coming soon. In the meantime, report any incident to status@vaultbypd.com." }],
    },
    "security/report-vulnerability": {
        eyebrow: "TRUST", title: "Report a Vulnerability",
        body: [
            { h: "Coordinated disclosure", p: "Email security@vaultbypd.com with details of the vulnerability. Please do not publicly disclose while we investigate. We commit to acknowledging within 3 business days." },
            { h: "Scope", p: "In-scope: vaultbypd.com, its subdomains, and the VAULT authenticated application." },
        ],
    },
    "about": {
        eyebrow: "COMPANY", title: "Why VAULT exists.",
        body: [
            { h: "One organised place", p: "VAULT was built by a finance operator who ran his own two businesses, forty-plus credit cards, an active investment book and a household — and got tired of scattered apps and broken spreadsheets." },
            { h: "Our promise", p: "One calm place. Real reconciliation. Real reminders. Real audit trail. No dark patterns, no fake bank-grade claims, no data misuse." },
        ],
    },
    "contact": {
        eyebrow: "COMPANY", title: "Get in touch.",
        contactForm: true,
        body: [
            { h: "Support", p: "support@vaultbypd.com — we aim to reply within one business day for all plans." },
            { h: "Privacy", p: "privacy@vaultbypd.com — data-subject requests and privacy queries." },
            { h: "Security", p: "security@vaultbypd.com — vulnerability reports and security concerns." },
            { h: "Sales", p: "sales@vaultbypd.com — Business plan enquiries and enterprise onboarding." },
        ],
    },
    "contact-sales": {
        eyebrow: "SALES", title: "Talk to VAULT for Business.",
        contactForm: true,
        contactTopicDefault: "sales",
        body: [
            { h: "Best fit", p: "Business plan is for owner-operated firms and finance teams that need Cash Desk, GST tagging, monthly closing, reconciliation and a CA-ready pack." },
            { h: "How to reach us", p: "Fill in the form below or email sales@vaultbypd.com directly — we'll respond within one business day." },
        ],
    },
    "help": {
        eyebrow: "HELP", title: "How can we help?",
        body: [
            { h: "In-app help", p: "Every module has a help pane accessible from the sidebar." },
            { h: "Email support", p: "support@vaultbypd.com — one business day response for all plans." },
        ],
    },
    "changelog": {
        eyebrow: "PRODUCT", title: "What's new.",
        body: [{ h: "Coming soon", p: "A public changelog is being prepared. Follow product updates via email — sign up during signup." }],
    },
    "roadmap": {
        eyebrow: "PRODUCT", title: "Where VAULT is going.",
        body: [{ h: "Coming soon", p: "A public roadmap is being prepared with our product priorities for the next two quarters." }],
    },
    "press": {
        eyebrow: "COMPANY", title: "Press Kit",
        body: [{ h: "Media enquiries", p: "For interviews, brand assets, screenshots and quotes, email press@vaultbypd.com." }],
    },
    "press-kit": {
        eyebrow: "COMPANY", title: "Press Kit",
        body: [
            { h: "Brand assets", p: "Wordmark, colour palette (dark navy · warm ivory · muted gold) and product screenshots are available on request." },
            { h: "Media enquiries", p: "For interviews and quotes, email press@vaultbypd.com. We respond within one business day." },
        ],
    },
    "support": {
        eyebrow: "SUPPORT", title: "We're here to help.",
        body: [
            { h: "In-app support", p: "Open the Help pane from the sidebar to search articles or contact us from any authenticated page." },
            { h: "Email us", p: "support@vaultbypd.com — we aim to reply within one business day for all plans." },
            { h: "Report an incident", p: "For live outages check /status. For security issues visit /security/report-vulnerability." },
        ],
    },
    "regional-privacy": {
        eyebrow: "LEGAL", title: "Regional Privacy Disclosures", legal: true,
        body: [
            { h: "India (DPDP Act, 2023)", p: "VAULT collects personal data for the purposes described in the Privacy Policy. You can exercise access, correction, portability and grievance rights via /privacy-centre and /grievance. Our Grievance Officer contact is published on the Grievance page." },
            { h: "European Union (GDPR)", p: "Where GDPR applies, we process personal data under Article 6(1)(b) contract necessity and Article 6(1)(f) legitimate interests. You may exercise access, rectification, erasure, portability, restriction and objection rights. Data Protection Officer contact: dpo@vaultbypd.com." },
            { h: "United Kingdom (UK GDPR)", p: "The same rights apply. UK representative details are available on request." },
            { h: "California (CCPA/CPRA)", p: "California residents may request the categories of personal information collected, request deletion, and opt out of the sale/share of personal information (we do not sell personal information)." },
            { h: "Other jurisdictions", p: "Additional regional notices are added as VAULT expands. Please contact privacy@vaultbypd.com if your jurisdiction requires specific disclosures." },
        ],
    },
    "data-processing-addendum": {
        eyebrow: "LEGAL", title: "Data Processing Addendum (DPA)", legal: true,
        body: [
            { h: "Availability", p: "A signed Data Processing Addendum is available for Business plan customers who process personal data of their own end-users in VAULT. Contact privacy@vaultbypd.com." },
            { h: "Scope", p: "The DPA covers processing instructions, subprocessor management, security measures, incident notification, international transfers (SCCs where applicable) and audit rights." },
        ],
    },
    "children-policy": {
        eyebrow: "LEGAL", title: "Children & Age Eligibility Policy", legal: true,
        body: [
            { h: "Minimum age", p: "VAULT is intended for individuals aged 18 or older. Users must confirm at signup that they meet the age requirement in their jurisdiction." },
            { h: "No data on minors", p: "VAULT does not knowingly collect personal information from anyone under 13 (US COPPA) or under the local minimum. Parents/guardians who believe a minor has created an account should email privacy@vaultbypd.com for deletion." },
        ],
    },
    "ai-disclosure": {
        eyebrow: "LEGAL", title: "AI & Automated Insights Disclosure", legal: true,
        body: [
            { h: "Where AI is used", p: "VAULT uses AI for: (a) the Finance Copilot chat assistant, (b) OCR of uploaded statements, (c) deduction optimiser suggestions, (d) money-leak and health-score insights. Every AI output is labelled as an 'Insight', never as professional advice." },
            { h: "External AI providers", p: "We currently route AI requests through Emergent LLM (OpenAI GPT / Anthropic Claude / Google Gemini families). See /subprocessors for the up-to-date list." },
            { h: "Model training", p: "Your data is not used to train third-party foundation models. Prompts and outputs are retained transiently for support and debugging (see /data-retention)." },
            { h: "Accuracy limitations", p: "AI outputs can be incomplete, out-of-date or wrong. Verify every figure and never act on an AI insight without checking the underlying source data." },
            { h: "How to disable", p: "Turn off AI features from Settings → Privacy → AI processing. Copilot chat is opt-in on signup." },
            { h: "Report an error", p: "Email privacy@vaultbypd.com with the session ID (shown on the AI Copilot header) so we can investigate." },
        ],
    },
    "testimonial-disclosure": {
        eyebrow: "LEGAL", title: "Testimonial & Review Disclosure", legal: true,
        body: [
            { h: "Only verified voices", p: "VAULT publishes only genuine, consented testimonials from real customers. Every quote is stored with dated consent evidence and the customer's approval." },
            { h: "No compensation unless disclosed", p: "If any customer receives compensation (discount, gift, credit) for a testimonial, this is explicitly disclosed on the quote." },
            { h: "Consent withdrawal", p: "Customers can withdraw their consent at any time by emailing privacy@vaultbypd.com; the testimonial is removed from the public site immediately." },
            { h: "Beta programme", p: "Until at least three approved testimonials exist, the site invites visitors to join the VAULT Global Beta Programme instead of displaying stock quotes." },
        ],
    },
    "grievance": {
        eyebrow: "SUPPORT", title: "Grievance Redressal",
        body: [
            { h: "How to raise a complaint", p: "Email grievance@vaultbypd.com with your account email, a description of the issue and any supporting details. You will receive a reference number." },
            { h: "Response times", p: "We aim to acknowledge every complaint within 3 business days and to resolve within 15 business days. Complex issues may take longer; we will keep you informed." },
            { h: "Contacts", list: [
                "Support: support@vaultbypd.com",
                "Privacy: privacy@vaultbypd.com",
                "Security: security@vaultbypd.com",
                "Billing: billing@vaultbypd.com",
            ] },
            { h: "Escalation", p: "If you are unsatisfied with the initial response, request escalation to the Grievance Officer at grievance-officer@vaultbypd.com. Contact details are published under administrator-approved wording only." },
            { h: "Not a regulated service", p: "VAULT is not a bank, NBFC, payment institution or investment adviser. Regulator-specific escalation channels (RBI Sachet, SEBI SCORES, etc.) do not apply to VAULT products." },
        ],
    },
    "data-retention": {
        eyebrow: "TRUST", title: "Data Retention",
        body: [
            { h: "Active accounts", p: "We retain your data for as long as your VAULT account is active and for a short period after cancellation, as required to complete billing, comply with law and defend legal claims." },
            { h: "Post-deletion", p: "When you delete your account, personal identifiers are removed within 30 days and anonymised transactional records may be retained for statistical use." },
            { h: "Backups", p: "Encrypted backups are retained for 30 days on a rolling basis and then rotated out." },
            { h: "Audit logs", p: "Security audit logs are retained for 12 months to support incident investigation." },
            { h: "Litigation hold", p: "Retention periods are extended where required by law, subpoena or ongoing dispute." },
        ],
    },
    "responsible-disclosure": {
        eyebrow: "TRUST", title: "Responsible Disclosure",
        body: [
            { h: "How to report", p: "Email security@vaultbypd.com with reproduction steps, impact assessment and any supporting artefacts. Use our PGP key on request." },
            { h: "Our commitment", p: "We acknowledge within 3 business days, keep you informed as we investigate and credit reporters who wish to be named after the fix ships." },
            { h: "Scope", p: "In-scope: vaultbypd.com, app.vaultbypd.com, api.vaultbypd.com. Out-of-scope: denial-of-service, social engineering, physical attacks and any third-party services." },
            { h: "Safe harbour", p: "Good-faith research under this policy will not result in legal action from VAULT." },
        ],
    },
    "incident-communications": {
        eyebrow: "TRUST", title: "Incident Communications",
        body: [
            { h: "How we notify", p: "For material incidents, we notify affected customers by email at the address on file and post an update on /status. Timing follows applicable regulatory requirements." },
            { h: "What we include", p: "Nature of the incident, systems and data types affected, current containment status, recommended user actions and next update time." },
            { h: "Post-mortem", p: "Once resolved, we publish a public post-mortem that describes root cause and remediation — without disclosing details that could enable further attacks." },
        ],
    },
    "press": {
        eyebrow: "COMPANY", title: "Press Kit",
        body: [{ h: "Media enquiries", p: "For interviews, brand assets, screenshots and quotes, email press@vaultbypd.com." }],
    },
    "press-kit": {
        eyebrow: "COMPANY", title: "Press Kit",
        body: [
            { h: "Brand assets", p: "Wordmark, colour palette (dark navy · warm ivory · muted gold) and product screenshots are available on request." },
            { h: "Media enquiries", p: "For interviews and quotes, email press@vaultbypd.com. We respond within one business day." },
        ],
    },
};

export default function PublicPage() {
    const { "*": splat } = useParams();
    const { t } = useLocale();
    const slug = window.location.pathname.replace(/^\/+/, "").replace(/\/$/, "");
    const key = PAGES[slug] ? slug : PAGES[splat] ? splat : null;
    const page = key ? PAGES[key] : null;

    if (!page) {
        return (
            <PublicShell>
                <div className="pp-container">
                    <div className="pp-eyebrow">404</div>
                    <h1 className="pp-title">Page not found.</h1>
                    <p className="pp-sub">The URL doesn&apos;t exist. Head back to the <Link to="/">homepage</Link>.</p>
                </div>
            </PublicShell>
        );
    }

    return (
        <PublicShell>
            <style>{styles}</style>
            <article className="pp-container" data-testid={`public-page-${key.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                <div className="pp-eyebrow">{t(`publicpage.${key}.eyebrow`) !== `publicpage.${key}.eyebrow` ? t(`publicpage.${key}.eyebrow`) : page.eyebrow}</div>
                <h1 className="pp-title">{t(`publicpage.${key}.title`) !== `publicpage.${key}.title` ? t(`publicpage.${key}.title`) : page.title}</h1>
                {(page.subtitle || t(`publicpage.${key}.subtitle`) !== `publicpage.${key}.subtitle`) && (
                    <p className="pp-sub">
                        {t(`publicpage.${key}.subtitle`) !== `publicpage.${key}.subtitle` ? t(`publicpage.${key}.subtitle`) : page.subtitle}
                    </p>
                )}

                {page.legal && (
                    <div className="pp-legal-notice">
                        Draft — awaiting final legal review. If you have a specific concern, email privacy@vaultbypd.com.
                    </div>
                )}

                {page.faq ? <FAQAccordion /> : (
                    <div className="pp-body">
                        {(page.body || []).map((b) => (
                            <section key={b.h} className="pp-section">
                                <h2 className="pp-h2">{b.h}</h2>
                                {b.p && <p className="pp-p">{b.p}</p>}
                                {b.list && (
                                    <ul className="pp-list">
                                        {b.list.map((l) => (
                                            <li key={l}><CheckCircle2 size={13} className="pp-check" /> {l}</li>
                                        ))}
                                    </ul>
                                )}
                            </section>
                        ))}
                    </div>
                )}

                {page.notice && <div className="pp-notice">{page.notice}</div>}
                {page.contactForm && <ContactForm defaultTopic={page.contactTopicDefault} />}
                {page.cta && (
                    <div className="pp-cta-row">
                        {page.cta.to ? (
                            <Link to={page.cta.to} className="pp-cta-btn">{page.cta.label} <ArrowRight size={14} /></Link>
                        ) : (
                            <button className="pp-cta-btn" onClick={() => window.dispatchEvent(new CustomEvent(page.cta.event))}>{page.cta.label}</button>
                        )}
                    </div>
                )}
            </article>
        </PublicShell>
    );
}

function FAQAccordion() {
    const [items, setItems] = useState([]);
    const [openIdx, setOpenIdx] = useState(0);
    useEffect(() => {
        api.get("/public-content/faq").then((r) => setItems(r.data?.items || [])).catch(() => setItems([]));
    }, []);
    return (
        <div className="pp-faq" data-testid="faq-accordion">
            {items.map((it, i) => {
                const open = openIdx === i;
                return (
                    <div key={i} className={`pp-faq-item ${open ? "pp-faq-item--open" : ""}`}>
                        <button className="pp-faq-q" onClick={() => setOpenIdx(open ? -1 : i)} data-testid={`faq-q-${i}`}>
                            <span>{it.q}</span>
                            {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </button>
                        {open && <div className="pp-faq-a">{it.a}</div>}
                    </div>
                );
            })}
            {items.length === 0 && <div className="pp-empty">Loading questions…</div>}
        </div>
    );
}

function ContactForm({ defaultTopic = "general" }) {
    const [f, setF] = useState({ name: "", email: "", subject: "", message: "", topic: defaultTopic });
    const [state, setState] = useState({ busy: false, done: false, err: "" });
    const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
    const submit = async (e) => {
        e.preventDefault();
        if (state.busy || state.done) return;
        setState({ busy: true, done: false, err: "" });
        try {
            const res = await api.post("/contact", f);
            setState({ busy: false, done: !!res.data?.ok, err: "" });
        } catch (err) {
            const msg = err?.response?.data?.detail || "Couldn't send. Please try again in a moment.";
            setState({ busy: false, done: false, err: typeof msg === "string" ? msg : "Couldn't send." });
        }
    };
    if (state.done) {
        return (
            <div className="pp-contact-done" data-testid="contact-form-success">
                <div className="pp-contact-done-h">Message received.</div>
                <div className="pp-contact-done-p">We&apos;ll reply to <b>{f.email}</b> within one business day. Every submission is also forwarded to the owner&apos;s inbox.</div>
            </div>
        );
    }
    return (
        <form onSubmit={submit} className="pp-contact-form" data-testid="contact-form">
            <div className="pp-contact-grid">
                <label className="pp-contact-field">
                    <span>Your name</span>
                    <input required minLength={1} maxLength={120} value={f.name} onChange={set("name")} data-testid="contact-name" />
                </label>
                <label className="pp-contact-field">
                    <span>Email</span>
                    <input required type="email" value={f.email} onChange={set("email")} data-testid="contact-email" />
                </label>
            </div>
            <label className="pp-contact-field">
                <span>Topic</span>
                <select value={f.topic} onChange={set("topic")} data-testid="contact-topic">
                    <option value="general">General enquiry</option>
                    <option value="sales">Sales / Business plan</option>
                    <option value="support">Product support</option>
                    <option value="privacy">Privacy / data request</option>
                    <option value="security">Security / vulnerability</option>
                    <option value="press">Press / media</option>
                </select>
            </label>
            <label className="pp-contact-field">
                <span>Subject</span>
                <input maxLength={180} value={f.subject} onChange={set("subject")} placeholder="One-line summary" data-testid="contact-subject" />
            </label>
            <label className="pp-contact-field">
                <span>Message</span>
                <textarea required rows={5} minLength={5} maxLength={4000} value={f.message} onChange={set("message")} data-testid="contact-message" />
            </label>
            {state.err && <div className="pp-contact-err" data-testid="contact-form-error">{state.err}</div>}
            <div className="pp-contact-actions">
                <button type="submit" disabled={state.busy} className="pp-cta-btn" data-testid="contact-submit">
                    {state.busy ? "Sending…" : "Send message"}
                </button>
                <div className="pp-contact-note">Goes straight to the owner. We never share your email.</div>
            </div>
        </form>
    );
}

const styles = `
.pp-container { max-width: 820px; margin: 0 auto; padding: 80px 28px 120px; color: #F6F3EC; }
.pp-eyebrow { font-size: 10.5px; font-weight: 700; letter-spacing: 0.24em; text-transform: uppercase; color: #C6A86B; margin-bottom: 20px; }
.pp-title { font-size: 42px; font-weight: 800; letter-spacing: -0.02em; line-height: 1.1; color: #F6F3EC; }
@media (min-width: 768px) { .pp-title { font-size: 56px; } .pp-container { padding: 120px 28px 160px; } }
.pp-sub { margin-top: 20px; font-size: 17px; line-height: 1.6; color: rgba(246,243,236,0.72); max-width: 700px; }
.pp-legal-notice { margin-top: 32px; padding: 14px 18px; border-radius: 12px; background: rgba(198,168,107,0.1); border: 1px solid rgba(198,168,107,0.25); color: #C6A86B; font-size: 13px; font-weight: 500; }
.pp-body { margin-top: 48px; display: flex; flex-direction: column; gap: 44px; }
.pp-h2 { font-size: 20px; font-weight: 700; color: #F6F3EC; margin-bottom: 12px; letter-spacing: -0.01em; }
.pp-p { font-size: 15.5px; line-height: 1.65; color: rgba(246,243,236,0.75); }
.pp-list { list-style: none; padding: 0; margin: 14px 0 0 0; display: flex; flex-direction: column; gap: 10px; }
.pp-list li { display: flex; align-items: flex-start; gap: 10px; font-size: 15px; color: rgba(246,243,236,0.85); }
.pp-check { color: #29C58A; margin-top: 5px; flex-shrink: 0; }
.pp-notice { margin-top: 40px; padding: 16px 20px; border-radius: 12px; background: rgba(246,243,236,0.04); font-size: 13px; color: rgba(246,243,236,0.6); border: 1px solid rgba(246,243,236,0.08); }
.pp-cta-row { margin-top: 40px; }
.pp-cta-btn { display: inline-flex; align-items: center; gap: 8px; padding: 12px 22px; border-radius: 999px; background: #C6A86B; color: #0B1122; font-size: 14px; font-weight: 700; border: none; cursor: pointer; }
.pp-cta-btn:hover { background: #d3b978; }
a { color: #C6A86B; }
a:hover { text-decoration: underline; }

.pp-faq { margin-top: 40px; }
.pp-faq-item { border-bottom: 1px solid rgba(246,243,236,0.08); }
.pp-faq-q { width: 100%; display: flex; align-items: center; justify-content: space-between; gap: 20px; padding: 22px 0; font-size: 16px; font-weight: 600; color: #F6F3EC; background: none; border: none; text-align: left; cursor: pointer; }
.pp-faq-q:hover { color: #C6A86B; }
.pp-faq-a { padding: 0 0 22px; font-size: 15px; color: rgba(246,243,236,0.72); line-height: 1.65; }
.pp-empty { padding: 30px 0; color: rgba(246,243,236,0.45); font-size: 14px; }

.pp-contact-form { margin-top: 44px; display: flex; flex-direction: column; gap: 18px; }
.pp-contact-grid { display: grid; grid-template-columns: 1fr; gap: 18px; }
@media (min-width: 720px) { .pp-contact-grid { grid-template-columns: 1fr 1fr; } }
.pp-contact-field { display: flex; flex-direction: column; gap: 6px; }
.pp-contact-field span { font-size: 10.5px; font-weight: 700; letter-spacing: 0.16em; text-transform: uppercase; color: rgba(246,243,236,0.55); }
.pp-contact-field input, .pp-contact-field select, .pp-contact-field textarea {
    background: rgba(246,243,236,0.06); color: #F6F3EC; border: 1px solid rgba(246,243,236,0.14);
    border-radius: 10px; padding: 12px 14px; font-size: 14px; font-family: inherit; outline: none;
    transition: border-color 0.15s;
}
.pp-contact-field input:focus, .pp-contact-field select:focus, .pp-contact-field textarea:focus { border-color: #C6A86B; }
.pp-contact-field textarea { resize: vertical; min-height: 120px; line-height: 1.5; }
.pp-contact-actions { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; margin-top: 4px; }
.pp-contact-note { font-size: 12px; color: rgba(246,243,236,0.5); }
.pp-contact-err { color: #f87171; font-size: 13px; padding: 10px 14px; background: rgba(248,113,113,0.08); border: 1px solid rgba(248,113,113,0.25); border-radius: 10px; }
.pp-contact-done { margin-top: 44px; padding: 24px 26px; background: rgba(41,197,138,0.08); border: 1px solid rgba(41,197,138,0.3); border-radius: 14px; }
.pp-contact-done-h { font-size: 18px; font-weight: 700; color: #F6F3EC; }
.pp-contact-done-p { font-size: 14px; color: rgba(246,243,236,0.78); margin-top: 6px; line-height: 1.55; }
`;
